import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { GetToast } from "../Components/Toast-Comps/GetToast";

const waitForImages = (element) => {
  const images = Array.from(element.querySelectorAll("img"));
  return Promise.all(
    images.map(
      (image) =>
        image.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              const finish = () => {
                clearTimeout(timer);
                image.removeEventListener("load", finish);
                image.removeEventListener("error", finish);
                resolve();
              };
              const timer = setTimeout(finish, 15000);
              image.addEventListener("load", finish, { once: true });
              image.addEventListener("error", finish, { once: true });
            })
    )
  );
};

const waitForPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

export const useSummaryPdfDownload = (pdfRef, filename = "download.pdf") => {
  const [isDownloading, setIsDownloading] = useState(false);
  const inFlight = useRef(false);

  const downloadPDF = async () => {
    if (inFlight.current) return;

    const input = pdfRef.current;
    if (!input || input.dataset.summaryReady !== "true") {
      GetToast("Summary is still loading. Please try again in a moment.", "error");
      return;
    }

    const orderIdentity = input.dataset.summaryOrder;
    const snapshot = input.innerHTML;
    const assertCurrent = () => {
      if (pdfRef.current !== input || !input.isConnected ||
          input.dataset.summaryReady !== "true" ||
          input.dataset.summaryOrder !== orderIdentity || input.innerHTML !== snapshot) {
        throw new Error("Summary changed during export; please retry");
      }
    };
    inFlight.current = true;
    setIsDownloading(true);
    try {
      await waitForPaint();
      await waitForImages(input);
      assertCurrent();

      if (!input.clientWidth || !input.clientHeight) {
        throw new Error("Summary has no rendered dimensions");
      }

      const canvas = await html2canvas(input, {
        useCORS: true,
        imageTimeout: 15000,
      });
      assertCurrent();

      if (!canvas.width || !canvas.height) {
        throw new Error("Summary capture returned an empty canvas");
      }

      const imgData = canvas.toDataURL("image/png");
      const pdfWidth = canvas.width * 0.264583;
      const pdfHeight = canvas.height * 0.264583;
      const pdf = new jsPDF("p", "mm", [pdfWidth, pdfHeight]);
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(filename);
    } catch (error) {
      console.error("Unable to download order summary PDF", error);
      GetToast("Unable to download the summary. Please try again.", "error");
    } finally {
      inFlight.current = false;
      setIsDownloading(false);
    }
  };

  return { downloadPDF, isDownloading };
};
